<template>
  <div>
    <div class="pg-head">
      <div class="pg-ico"><span class="mdi mdi-clipboard-alert-outline"></span></div>
      <div>
        <div class="id-h1">{{ $t('ops.title') }}</div>
        <div class="pg-sub">{{ $t('ops.subtitle') }}</div>
      </div>
      <div class="grid-spacer"></div>
      <span class="badge badge-warning" style="align-self:center">
        <span class="mdi mdi-lock-outline"></span> {{ $t('ops.managerOnly') }}
      </span>
      <button class="id-btn id-btn-soft" :disabled="loading" @click="fetch(false, true); fetchTrend()">
        <span class="mdi" :class="loading ? 'mdi-loading mdi-spin' : 'mdi-refresh'"></span>
        {{ $t('ops.refresh') }}
      </button>
    </div>

    <!-- แถบสรุป + หน้าต่างเวลา -->
    <div class="ops-bar">
      <button class="ops-chip s-urgent" :class="{ on: filter === 'urgent' }" @click="toggleFilter('urgent')">
        <span class="mdi mdi-alert-circle"></span>
        {{ $t('ops.sevUrgent') }} <b>{{ totals.urgent }}</b>
      </button>
      <button class="ops-chip s-watch" :class="{ on: filter === 'watch' }" @click="toggleFilter('watch')">
        <span class="mdi mdi-alert-outline"></span>
        {{ $t('ops.sevWatch') }} <b>{{ totals.watch }}</b>
      </button>
      <button class="ops-chip s-info" :class="{ on: filter === 'info' }" @click="toggleFilter('info')">
        <span class="mdi mdi-information-outline"></span>
        {{ $t('ops.sevInfo') }} <b>{{ totals.info }}</b>
      </button>
      <span class="fin-sep"></span>
      <span class="t-cap">{{ $t('ops.lookback') }}</span>
      <id-select class="grid-filter" v-model="lookbackDays" :options="lookbackOptions"
        @update:model-value="fetch(false, true)"></id-select>
      <div class="grid-spacer"></div>
      <span v-if="generatedAt" class="t-cap">{{ $t('ops.checkedAt', { time: generatedAt }) }}</span>
    </div>

    <!-- ⚠️ ระบบไม่มีสถานะ "ขาดเรียน" (มีแค่เช็คชื่อแล้ว/ยังไม่เช็ค) — เดิมข้อความนี้บอกว่า
         "กดเคลียร์ไม่ได้" ซึ่งทำให้แอดมินคิดว่าจนมุม · ที่จริงเคลียร์ได้ 2 ทางที่มีอยู่แล้ว
         (เช็คชื่อย้อนหลัง / ยกเลิกใบจองแล้วคืนครั้ง) จึงเปลี่ยนมาสอนวิธีทำแทน — เจ้าของเคาะ
         2026-08-05 พร้อมกับตัดสินใจ "ไม่ทำปุ่มไม่มา" เพราะร้านใจดี ยกเลิกใบจองให้เสมอ -->
    <div class="fin-note ops-note">
      <span class="mdi mdi-information-outline"></span>
      {{ $t('ops.attendanceLimitNote') }}
    </div>

    <!-- แนวโน้ม 30 วัน — ซ่อนทั้งการ์ดเมื่อยังมีข้อมูลไม่ถึง 2 วัน (ก่อนรัน migration หรือ
         เพิ่งเริ่มถ่ายภาพวันแรก) จุดเดียวลากเส้นแนวโน้มไม่ได้ และการ์ดเปล่าทำให้คนคิดว่าพัง -->
    <div v-if="trendDays.length >= 2" class="scard ops-trend">
      <button type="button" class="ops-trend-head" :aria-expanded="showTrend" @click="showTrend = !showTrend">
        <span class="mdi mdi-chart-line"></span>
        <span style="flex:1;text-align:left">
          <span class="ops-card-t">{{ $t('ops.trendTitle', { n: trendDays.length }) }}</span>
          <span class="ops-card-d">{{ $t('ops.trendNote', { days: trendLookback }) }}</span>
        </span>
        <span class="mdi" :class="showTrend ? 'mdi-chevron-up' : 'mdi-chevron-down'"></span>
      </button>

      <div v-if="showTrend" class="ops-trend-body id-fade-in">
        <div class="ops-trend-legend">
          <span v-for="s in SEVS" :key="s" class="ops-trend-lg">
            <i class="ops-trend-sw" :class="'s-' + s"></i>{{ $t('ops.sev' + s.charAt(0).toUpperCase() + s.slice(1)) }}
            <b>{{ trendLast(s) }}</b>
            <span v-if="trendDelta(s) !== 0" class="badge" :class="trendDelta(s) > 0 ? 'badge-error' : 'badge-success'">
              <span class="mdi" :class="trendDelta(s) > 0 ? 'mdi-arrow-up-bold' : 'mdi-arrow-down-bold'"></span>
              {{ Math.abs(trendDelta(s)) }}
            </span>
          </span>
        </div>

        <!-- ⚠️ เดิมมีแต่เส้น ไม่มีจุด และพื้นที่รับเมาส์กว้างแค่ 16px ต่อจุด → ต้องเล็งหาว่าจะชี้ตรงไหน
             (เจ้าของทัก 5 ส.ค.) แก้ 3 อย่าง: (1) มีจุดให้เห็นทุกข้อมูล (2) แถบรับเมาส์กินเต็มความกว้าง
             ของแต่ละวันแบบไม่มีช่องว่าง เอาเมาส์ไปตรงไหนก็ติด (3) มีเส้นแนวตั้งบอกว่ากำลังอ่านวันไหนอยู่ -->
        <div class="ops-spark-wrap" @mouseleave="tipIdx = -1">
          <svg :viewBox="'0 0 ' + SPARK_W + ' ' + SPARK_H" preserveAspectRatio="none" class="ops-spark">
            <polyline v-for="s in SEVS" :key="s" :points="polyline(s)" fill="none"
              :class="'s-' + s" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
          </svg>

          <!-- เส้นแนวตั้งของวันที่กำลังชี้ -->
          <div v-if="tipIdx >= 0" class="ops-spark-guide" :style="{ left: hitLeft(tipIdx) + '%' }"></div>

          <!-- จุดข้อมูล: วาดเป็น HTML ไม่ใช่ <circle> เพราะ svg ถูกยืดด้วย preserveAspectRatio="none"
               วงกลมใน svg จะถูกบีบเป็นวงรี — HTML absolute คุมขนาดเป็น px ได้ตรง ๆ -->
          <template v-for="s in SEVS" :key="s">
            <span v-for="(d, i) in trendDays" :key="s + d.date"
              v-show="showDot(i)" class="ops-spark-dot"
              :class="['s-' + s, { on: tipIdx === i }]"
              :style="{ left: hitLeft(i) + '%', top: dotTop(d.totals[s]) + '%' }"></span>
          </template>

          <!-- แถบรับเมาส์ 1 แถบต่อ 1 วัน ต่อกันสนิทเต็มความกว้าง (ไม่มีรู) -->
          <div v-for="(d, i) in trendDays" :key="'hit' + d.date" class="ops-spark-hit"
            :style="{ left: hitBand(i).left + '%', width: hitBand(i).width + '%' }"
            tabindex="0" :aria-label="tipText(i)"
            @mouseenter="tipIdx = i" @focus="tipIdx = i" @blur="tipIdx = -1"></div>

          <div v-if="tipIdx >= 0" class="ops-spark-tip" :class="tipAlign(tipIdx)"
            :style="{ left: hitLeft(tipIdx) + '%', transform: tipShift(tipIdx) }">
            <b>{{ $formatShortDate(trendDays[tipIdx].date) }}</b>
            <span v-for="s in SEVS" :key="s">
              <i class="ops-trend-sw" :class="'s-' + s"></i>
              {{ $t('ops.sev' + s.charAt(0).toUpperCase() + s.slice(1)) }} {{ trendDays[tipIdx].totals[s] }}
            </span>
          </div>
        </div>

        <!-- ตัวเลขเต็มโดยไม่ต้อง hover — hover อย่างเดียวใช้ไม่ได้บนมือถือ -->
        <button type="button" class="spark-toggle" :aria-expanded="showTrendTable"
          @click="showTrendTable = !showTrendTable">
          <span class="mdi" :class="showTrendTable ? 'mdi-chevron-up' : 'mdi-chevron-down'"></span>
          {{ showTrendTable ? $t('ops.trendHideTable') : $t('ops.trendShowTable') }}
        </button>
        <div v-if="showTrendTable" style="overflow-x:auto">
          <table class="idt" style="margin-top:6px">
            <thead>
              <!-- ห้ามใส่ text-align ทับ: `.idt th` เป็น left และ `.idt td` ก็ left
                   ใส่ center เฉพาะหัวตารางเมื่อไหร่ เลขจะไม่ตรงหัวคอลัมน์ทันที (เจ้าของเจอ 5 ส.ค.) -->
              <tr>
                <th>{{ $t('ops.trendColDate') }}</th>
                <th v-for="s in SEVS" :key="s">
                  {{ $t('ops.sev' + s.charAt(0).toUpperCase() + s.slice(1)) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="d in [...trendDays].reverse()" :key="d.date">
                <td>{{ $formatShortDate(d.date) }}</td>
                <td v-for="s in SEVS" :key="s" class="idt-num">{{ d.totals[s] }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <template v-if="loading && !loaded">
      <div class="scard" v-for="n in 4" :key="'sk' + n" style="margin-top:12px">
        <div class="id-skel" style="height:64px"></div>
      </div>
    </template>

    <template v-else>
      <!-- ทุกอย่างเคลียร์ -->
      <div v-if="!openGroups.length" class="scard ops-clear">
        <span class="mdi mdi-check-decagram"></span>
        <div>
          <div class="ops-clear-t">{{ $t('ops.allClear') }}</div>
          <div class="t-cap">{{ $t('ops.allClearSub') }}</div>
        </div>
      </div>

      <div v-for="g in openGroups" :key="g.key" class="scard ops-card" :class="'sev-' + g.severity">
        <button type="button" class="ops-card-head" @click="toggle(g.key)">
          <span class="ops-sev-dot" :class="'s-' + g.severity"></span>
          <span class="ops-card-tx">
            <span class="ops-card-t">{{ label(g.key) }}</span>
            <span class="ops-card-d">{{ hint(g.key) }}</span>
          </span>
          <span class="ops-count" :class="'s-' + g.severity">{{ g.count }}</span>
          <span v-if="g.amount" class="ops-amount">{{ baht(g.amount) }}</span>
          <span class="mdi ops-chev" :class="expanded[g.key] ? 'mdi-chevron-up' : 'mdi-chevron-down'"></span>
        </button>

        <div v-if="expanded[g.key]" class="ops-card-body">
          <!-- แถบไปหน้าที่ใช้แก้เรื่องนี้ — ทุกรายการต้องมีทางออก ไม่ใช่แค่บอกว่ามีปัญหา -->
          <div class="ops-actions">
            <button v-if="gotoState(g.key)" class="id-btn id-btn-soft id-btn-sm"
              @click="$emit('onClickChangeState', gotoState(g.key))">
              <span class="mdi mdi-open-in-new"></span> {{ $t('ops.goFix') }}
            </button>
            <!-- ตัดรายการ = ต้องบอก ห้ามตัดเงียบ ๆ แล้วให้คนอ่านคิดว่าเห็นครบแล้ว -->
            <span v-if="g.truncated" class="badge badge-warning">
              <span class="mdi mdi-dots-horizontal"></span>
              {{ $t('ops.truncated', { shown: g.items.length, total: g.count }) }}
            </span>
            <!-- ใบไม่รู้ราคาถูกตีเป็น 0 ในยอดรวม — ต้องบอก ไม่งั้นจำนวนกับยอดเงินขัดกันเองเงียบ ๆ -->
            <span v-if="g.unknownPriceCount" class="badge badge-warning">
              <span class="mdi mdi-alert-outline"></span>
              {{ $t('ops.unknownPriceExcluded', { n: g.unknownPriceCount }) }}
            </span>
            <!-- ใบที่ยอดยังขาดแต่เพิ่งแนบสลิป = กำลังทยอยโอน ไม่ใช่ความผิดพลาด — บอกไว้ไม่ให้ไล่ตามผิดใบ -->
            <span v-if="g.partialCount" class="badge badge-info">
              <span class="mdi mdi-progress-clock"></span>
              {{ $t('ops.slipPartialNote', { n: g.partialCount }) }}
            </span>
            <span v-if="g.since" class="t-cap">{{ $t('ops.countedSince', { date: $formatShortDate(g.since) }) }}</span>
          </div>

          <div style="overflow-x:auto">
            <table class="idt">
              <thead>
                <tr>
                  <th v-for="c in cols(g.key)" :key="c.k">{{ $t('ops.col.' + c.k) }}</th>
                </tr>
              </thead>
              <tbody class="id-fade-in" :key="g.key + ':' + page(g.key)">
                <tr v-for="(row, i) in pagedItems(g)" :key="i">
                  <td v-for="c in cols(g.key)" :key="c.k" :class="c.cls">
                    <!-- เลขที่ (courserefer / refer) = ค่าที่แอดมินต้องเอาไปวางต่อในหน้าอื่นบ่อยที่สุด
                         จึงมีปุ่มคัดลอกเหมือนหน้าคอร์สของลูกค้า · ค่าว่างแสดง "—" เฉย ๆ ไม่มีปุ่ม -->
                    <span v-if="c.k === 'ref' && cell(row, c, g.key) !== '—'" class="id-copy">
                      {{ cell(row, c, g.key) }}
                      <span class="mdi mdi-content-copy id-copy-btn" v-hint="$t('btn.copy')"
                        @click="copyToClipboard(cell(row, c, g.key))"></span>
                    </span>
                    <template v-else>{{ cell(row, c, g.key) }}</template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <id-grid-footer v-if="g.items.length > perPage"
            :page="page(g.key)" :page-count="Math.max(1, Math.ceil(g.items.length / perPage))"
            :per-page="perPage" :total="g.items.length"
            @update:page="p => setPage(g.key, p)"
            @update:per-page="n => { perPage = n; setPage(g.key, 1); }"></id-grid-footer>
        </div>
      </div>

      <!-- หัวข้อที่ผ่านแล้ว — ยุบไว้ท้ายหน้า เพื่อให้เห็นว่า "ตรวจแล้ว" ไม่ใช่ "ไม่ได้ตรวจ" -->
      <div v-if="clearGroups.length" class="scard ops-ok">
        <div class="ops-ok-head">
          <span class="mdi mdi-check-circle-outline"></span>
          {{ $t('ops.passed') }} ({{ clearGroups.length }})
        </div>
        <div class="ops-ok-list">
          <span v-for="g in clearGroups" :key="g.key" class="ops-ok-item">
            <span class="mdi mdi-check"></span> {{ label(g.key) }}
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { formatBaht } from '@/utils/money';
import { intlLocale } from '@/utils/locale';

// เหมือนหน้าอื่นในระบบ: โพลเงียบทุก 60 วิ
const POLL_MS = 60000;

// คอลัมน์ต่อรายการตรวจ — key ใช้เป็นทั้งคีย์ i18n (ops.col.<k>) และชื่อฟิลด์ default
// f = ชื่อฟิลด์จริงใน row ถ้าต่างจาก k · cls = 'idt-num' สำหรับคอลัมน์ตัวเลข
const COLS = {
  'checkin.group': [
    { k: 'date', f: 'classdate', type: 'date' }, { k: 'time', f: 'classtime' },
    { k: 'course', f: 'coursename' }, { k: 'pending', cls: 'idt-num' },
    { k: 'age', f: 'ageDays', type: 'age' },
  ],
  'checkin.private': [
    { k: 'date', f: 'bookingdate', type: 'date' }, { k: 'time', f: 'classtime' },
    { k: 'coach', f: 'coachname' }, { k: 'students' }, { k: 'age', f: 'ageDays', type: 'age' },
  ],
  'checkin.event': [
    { k: 'date', f: 'eventdate', type: 'date' }, { k: 'time', f: 'starttime' },
    { k: 'event', f: 'eventname' }, { k: 'pending', cls: 'idt-num' },
    { k: 'age', f: 'ageDays', type: 'age' },
  ],
  'package.unlinked': [
    { k: 'ref', f: 'courserefer' }, { k: 'course', f: 'coursename' }, { k: 'type', f: 'coursetype' },
    { k: 'amount', f: 'price', type: 'money' }, { k: 'paid', type: 'paid' },
    { k: 'created', f: 'createdate', type: 'date' }, { k: 'by', f: 'createby' },
  ],
  'payment.unpaidTaught': [
    { k: 'kind', type: 'kind' }, { k: 'ref' }, { k: 'name' }, { k: 'students' },
    { k: 'sessions', cls: 'idt-num' }, { k: 'amount', type: 'money' },
    { k: 'since', type: 'date' }, { k: 'age', f: 'ageDays', type: 'age' },
  ],
  'payment.unpaid': [
    { k: 'kind', type: 'kind' }, { k: 'ref' }, { k: 'name' }, { k: 'students' },
    { k: 'amount', type: 'money' }, { k: 'since', type: 'date' },
  ],
  'price.missing': [
    { k: 'ref', f: 'courserefer' }, { k: 'course', f: 'coursename' }, { k: 'type', f: 'coursetype' },
    { k: 'paid', type: 'paid' }, { k: 'created', f: 'createdate', type: 'date' },
    { k: 'suggested', f: 'suggestedPrice', type: 'money' },
  ],
  'payment.methodMissing': [
    { k: 'kind', type: 'kind' }, { k: 'ref' }, { k: 'name' },
    { k: 'amount', type: 'money' }, { k: 'since', type: 'date' },
  ],
  'slip.unverified': [
    { k: 'context' }, { k: 'ref', f: 'refer' }, { k: 'status', f: 'verify_status' },
    { k: 'reason' }, { k: 'amount', type: 'money' }, { k: 'sender', f: 'sender_name' },
    { k: 'created', f: 'createdate' },
  ],
  // สลิปไม่สอดคล้องกับรายการ (multi-slip §5.1) — คนละคำถามกับ slip.unverified ข้างบน:
  // ตัวนั้นถาม "สลิปแท้ไหม" (SlipOK) ตัวนี้ถาม "ตรงกับที่แอดมินกรอกไหม" (ยอดรวม/วันที่)
  'slip.inconsistent': [
    { k: 'kind', type: 'kind' }, { k: 'ref' }, { k: 'name' },
    { k: 'reason', type: 'slipReason' },
    { k: 'slipTotal', f: 'slip_total', type: 'money' }, { k: 'price', type: 'money' },
    { k: 'paydate', type: 'date' },
  ],
  // ชำระแล้ว (โอน/บัตร) แต่ไม่มีสลิปแนบเลย — ลงบัญชี Peak หาสลิปไม่เจอ (เจ้าของสั่ง 2026-10-12)
  'slip.missing': [
    { k: 'kind', type: 'kind' }, { k: 'ref' }, { k: 'students' }, { k: 'name' },
    { k: 'method', type: 'method' }, { k: 'amount', type: 'money' }, { k: 'paydate', f: 'since', type: 'date' },
  ],
  'package.expiringSoon': [
    { k: 'ref', f: 'courserefer' }, { k: 'course', f: 'coursename' }, { k: 'students' },
    { k: 'remaining', cls: 'idt-num' }, { k: 'expire', f: 'expiredate', type: 'date' },
    { k: 'paid', type: 'paid' },
  ],
  'package.stale': [
    { k: 'ref', f: 'courserefer' }, { k: 'course', f: 'coursename' }, { k: 'students' },
    { k: 'remaining', cls: 'idt-num' }, { k: 'created', f: 'createdate', type: 'date' },
    { k: 'paid', type: 'paid' },
  ],
  'leave.pending': [
    { k: 'staff', f: 'staffname' }, { k: 'leaveType', f: 'leavetype' },
    { k: 'from', f: 'startdate', type: 'date' }, { k: 'to', f: 'enddate', type: 'date' },
    { k: 'requested', f: 'createdate', type: 'date' }, { k: 'age', f: 'ageDays', type: 'age' },
  ],
  'student.pendingApproval': [
    { k: 'student', f: 'fullname' }, { k: 'nickname' }, { k: 'requested', f: 'createdate', type: 'date' },
  ],
  // ผู้จัดการต้องตอบให้ได้ 3 อย่างจากแถวเดียว: อนุมัติใครเข้ามา · ใครเป็นคนกด · ซ้ำกับใครเพราะอะไร
  'student.approvedDuplicate': [
    { k: 'student' }, { k: 'kind', type: 'kind' }, { k: 'approver' },
    { k: 'matched', type: 'dupMatches' }, { k: 'approvedAt', type: 'date' },
  ],
  'conflict.coachLeave': [
    { k: 'date', f: 'bookingdate', type: 'date' }, { k: 'time', f: 'classtime' },
    { k: 'coach', f: 'coachname' }, { k: 'type', f: 'privatetype' },
  ],
  'conflict.holiday': [
    { k: 'date', f: 'eventdate', type: 'date' }, { k: 'time', f: 'classtime' },
    { k: 'course', f: 'coursename' }, { k: 'pending', f: 'n', cls: 'idt-num' },
    { k: 'holiday', f: 'description' },
  ],
  'package.expiredWithSessions': [
    { k: 'ref', f: 'courserefer' }, { k: 'course', f: 'coursename' }, { k: 'students' },
    { k: 'remaining', cls: 'idt-num' }, { k: 'expire', f: 'expiredate', type: 'date' },
    { k: 'age', f: 'ageDays', type: 'age' },
  ],
  // อีเวนต์ v2 เฟส 3 — ใบสมัครค้างชำระ (กินที่นั่งอยู่) + เงินผู้จัดที่ยังไม่บันทึกนำส่ง
  'event.unpaid': [
    { k: 'event', f: 'eventlabel' }, { k: 'date', f: 'eventdate', type: 'date' },
    { k: 'nickname' }, { k: 'amount', type: 'money' },
    { k: 'deadline', f: 'reg_close', type: 'date' }, { k: 'pastDeadline', type: 'flag' },
  ],
  'event.organizerRemit': [
    { k: 'event', f: 'eventlabel' }, { k: 'date', f: 'eventdate', type: 'date' },
    { k: 'collected', type: 'money' }, { k: 'remitted', type: 'money' },
    { k: 'outstanding', type: 'money' },
  ],
  // เฟส 5 — ใบที่ยกเลิกแล้วแต่เงินยังอยู่ในมือยิม ยังไม่มีใครตัดสินว่าคืนหรือไม่คืน
  'event.refundPending': [
    { k: 'event', f: 'eventlabel' }, { k: 'date', f: 'eventdate', type: 'date' },
    { k: 'nickname' }, { k: 'amount', type: 'money' },
    { k: 'paydate', f: 'paydate', type: 'date' }, { k: 'cancelledate', f: 'cancelledate', type: 'date' },
  ],
};

// หน้าที่จะพาไปแก้เรื่องนั้น ๆ (state ของ App.vue) — ปลายทางถูกกรองด้วย isStateAllowed อีกชั้น
const GOTO = {
  'checkin.group': 'bookingmanager',
  'checkin.private': 'privatebookingmanager',
  'checkin.event': 'eventmanager',
  'package.unlinked': 'customercourse',
  'payment.unpaidTaught': 'customercourse',
  'payment.unpaid': 'customercourse',
  'price.missing': 'customercourse',
  'payment.methodMissing': 'customercourse',
  'slip.unverified': 'customercourse',
  'slip.inconsistent': 'customercourse',
  'slip.missing': 'customercourse',
  'package.expiringSoon': 'customercourse',
  'package.stale': 'customercourse',
  'leave.pending': 'leaveapproval',
  'student.pendingApproval': 'dashboard',
  'student.approvedDuplicate': 'gymnastmanager',
  'conflict.coachLeave': 'privatebookingmanager',
  'conflict.holiday': 'holidaymanager',
  'package.expiredWithSessions': 'customercourse',
  'event.unpaid': 'eventmanager',
  'event.organizerRemit': 'eventmanager',
  'event.refundPending': 'eventmanager',
};

// เรียงความสำคัญ: แดง -> เหลือง -> ฟ้า
const SEV_ORDER = { urgent: 0, watch: 1, info: 2 };

// กราฟแนวโน้ม: viewBox คงที่แล้วให้ svg ยืดเต็มความกว้าง (preserveAspectRatio="none")
// เส้นไม่บิดเพราะใช้ vector-effect="non-scaling-stroke"
const SPARK_W = 300;
const SPARK_H = 96;
// เว้นขอบบน/ล่างให้จุดที่ค่าสูงสุด/ต่ำสุดไม่ถูกตัดครึ่ง (จุดมีรัศมีรวมขอบขาว ~5.5px)
const SPARK_PAD = 8;
// เกินกี่วันแล้วเลิกโชว์จุดทุกวัน (ดู showDot) — 14 วันบนมือถือ = จุดห่างกัน ~19px ยังอ่านออก
const DOT_DENSITY_MAX = 14;

export default {
  name: 'OpsChecklist',
  emits: ['onClickChangeState', 'onErrorHandler'],
  data() {
    return {
      loading: false,
      loaded: false,
      groups: [],
      totals: { urgent: 0, watch: 0, info: 0 },
      generatedAt: '',
      lookbackDays: 14,
      filter: '',
      expanded: {},
      pages: {},
      perPage: 20,
      interval: null,
      // แนวโน้ม 30 วัน — ข้อมูลเปลี่ยนวันละครั้ง (cron 21:30) จึงไม่รวมในโพล 60 วิ
      trendDays: [],
      trendLookback: 14,
      showTrend: false,      // default หุบ — ของหลักของหน้านี้คือรายการที่ต้องไปแก้ ไม่ใช่กราฟ
      showTrendTable: false,
      tipIdx: -1,
    };
  },
  computed: {
    lookbackOptions() {
      return [7, 14, 30, 90].map((n) => ({ value: n, label: this.$t('ops.lookbackDays', { n }) }))
        .concat([{ value: 0, label: this.$t('ops.lookbackAll') }]);
    },
    // หัวข้อที่ยังมีของค้าง เรียงตามความเร่งด่วนแล้วจำนวน
    openGroups() {
      return this.groups
        .filter((g) => g.count > 0 && (!this.filter || g.severity === this.filter))
        .sort((a, b) => (SEV_ORDER[a.severity] - SEV_ORDER[b.severity]) || (b.count - a.count));
    },
    clearGroups() {
      return this.groups.filter((g) => g.count === 0 && (!this.filter || g.severity === this.filter));
    },
    SEVS() { return ['urgent', 'watch', 'info']; },
    SPARK_W() { return SPARK_W; },
    SPARK_H() { return SPARK_H; },
    // สเกลร่วมกันทั้งสามเส้น — ถ้าแยกสเกลต่อเส้น เส้นที่มีของ 2 รายการจะดูสูงเท่าเส้นที่มี 80 รายการ
    trendMax() {
      let m = 1;
      for (const d of this.trendDays) {
        for (const s of this.SEVS) m = Math.max(m, Number(d.totals[s]) || 0);
      }
      return m;
    },
  },
  created() {
    // ⚠️ เข้าหน้า = force เสมอ (ไม่ใช้ cache) — เจ้าของเพิ่งเจอบั๊ก "แก้ข้อมูลแล้วตัวเลขไม่ขยับ"
    // มาหมาด ๆ ถ้าเข้าหน้าแล้วยังได้ค่าเก่าจาก cache 60 วิ จะดูเหมือนบั๊กเดิมกลับมาทันที
    // cache ยังทำงานเต็มที่กับสิ่งที่มันตั้งใจแก้จริง ๆ คือ "โพลเบื้องหลัง" ซึ่งมีเยอะกว่ามาก
    // (เข้าหน้า 1 ครั้ง : โพล 60 ครั้ง/ชม./แท็บ)
    this.fetch(false, true);
    this.fetchTrend();
  },
  mounted() {
    this.interval = setInterval(() => this.fetch(true), POLL_MS);
  },
  beforeUnmount() {
    clearInterval(this.interval);
  },
  methods: {
    // force = ข้าม cache 60 วิฝั่งเซิร์ฟเวอร์ · **ส่งเฉพาะตอนกดปุ่ม "ตรวจใหม่" เท่านั้น**
    // การเปิดหน้า/เปลี่ยนช่วงย้อนหลัง/โพลเบื้องหลัง ต้องใช้ cache ได้ ไม่งั้น cache แทบไม่มีผล
    // (เข้าเมนูทีไรก็สั่งคำนวณใหม่ทุกครั้ง ซึ่งคือสิ่งที่ cache ตั้งใจจะเลี่ยง)
    async fetch(silent, force) {
      const token = this.$store.getters.getToken;
      if (!token) return;
      if (!silent) this.loading = true;
      // audit E15: จำค่าช่วงย้อนหลังตอนยิง — ถ้าผู้ใช้เปลี่ยนระหว่างรอ ให้ทิ้งคำตอบเก่า
      const requestedLookback = this.lookbackDays;
      try {
        const res = await axios.post(this.baseURL + '/getOpsChecklist',
          { lookbackDays: requestedLookback, force: !!force },
          { headers: { Authorization: 'Bearer ' + token } });
        if (requestedLookback !== this.lookbackDays) return;
        if (res.data && res.data.success) {
          this.groups = res.data.groups || [];
          this.totals = res.data.totals || { urgent: 0, watch: 0, info: 0 };
          this.generatedAt = new Date().toLocaleTimeString(intlLocale(), { hour: '2-digit', minute: '2-digit' });
          this.loaded = true;
        }
      } catch (e) {
        // โพลเบื้องหลังห้ามเด้ง dialog; เฉพาะการกดโหลดเองที่แจ้งผู้ใช้
        if (!silent) this.$emit('onErrorHandler', this.$t('ops.loadFail'));
      } finally {
        // คำขอเก่าห้ามไปดับ skeleton ของคำขอใหม่ที่เพิ่งยิงแทน
        if (requestedLookback === this.lookbackDays) this.loading = false;
      }
    },
    // ── แนวโน้ม 30 วัน ────────────────────────────────────────────────────
    // ⚠️ ตั้งใจ **ไม่** เรียกใน poll 60 วิ — ข้อมูลถูกถ่ายวันละครั้งตอน 21:30 โพลถี่ ๆ จึงเปลืองเปล่า
    // เรียกตอนเปิดหน้า + ตอนกดปุ่มรีเฟรชเท่านั้น · ล้มเหลวต้องเงียบเสมอ: การ์ดนี้เป็นของเสริม
    // ห้ามให้ backend เก่า/ยังไม่ได้รัน migration ทำให้หน้าหลักขึ้น error dialog
    async fetchTrend() {
      const token = this.$store.getters.getToken;
      if (!token) return;
      try {
        const res = await axios.post(this.baseURL + '/getOpsTrend', {},
          { headers: { Authorization: 'Bearer ' + token } });
        if (res.data && res.data.success) {
          this.trendDays = res.data.days || [];
          if (res.data.lookbackDays) this.trendLookback = res.data.lookbackDays;
        }
      } catch (e) {
        this.trendDays = [];
      }
    },
    trendLast(sev) {
      const d = this.trendDays[this.trendDays.length - 1];
      return d ? (Number(d.totals[sev]) || 0) : 0;
    },
    // เทียบวันล่าสุดกับวันก่อนหน้า — บวก = ของค้างเพิ่มขึ้น (แดง) · ลบ = เคลียร์ได้ (เขียว)
    trendDelta(sev) {
      const n = this.trendDays.length;
      if (n < 2) return 0;
      return (Number(this.trendDays[n - 1].totals[sev]) || 0)
        - (Number(this.trendDays[n - 2].totals[sev]) || 0);
    },
    sparkX(i) {
      const n = this.trendDays.length;
      return n > 1 ? SPARK_PAD + (i * (SPARK_W - 2 * SPARK_PAD)) / (n - 1) : SPARK_W / 2;
    },
    sparkY(v) {
      return SPARK_H - SPARK_PAD - ((Number(v) || 0) / this.trendMax) * (SPARK_H - 2 * SPARK_PAD);
    },
    polyline(sev) {
      return this.trendDays.map((d, i) => `${this.sparkX(i)},${this.sparkY(d.totals[sev])}`).join(' ');
    },
    // ตำแหน่งเป็น % ของกรอบจริง (svg ถูกยืดเต็มกรอบ จึงใช้ px ของ viewBox ไม่ได้)
    hitLeft(i) { return (this.sparkX(i) / SPARK_W) * 100; },
    dotTop(v) { return (this.sparkY(v) / SPARK_H) * 100; },
    // ความหนาแน่นของจุด: ช่วงแรก ๆ (ข้อมูลไม่กี่วัน) โชว์ทุกจุดเพื่อให้เห็นว่ามีข้อมูลตรงไหนบ้าง
    // พอครบ 30 วันแล้ว 30 วัน × 3 เส้น = 90 จุด บนมือถือกว้าง 262px จุดจะเบียดกันจนกลายเป็นเส้นประ
    // อ่านไม่ออก → เหลือเฉพาะจุดของวันล่าสุด (จุดยึดสายตา) กับวันที่กำลังชี้อยู่
    // ตัวเส้นเองยังบอกรูปทรงแนวโน้มได้ครบ และตัวเลขเป๊ะ ๆ ดูได้จากตารางด้านล่างอยู่แล้ว
    showDot(i) {
      return this.trendDays.length <= DOT_DENSITY_MAX
        || i === this.trendDays.length - 1
        || this.tipIdx === i;
    },
    // แถบรับเมาส์ของวันที่ i = ครึ่งทางจากวันก่อนหน้า ถึงครึ่งทางไปวันถัดไป · วันหัว/ท้ายยืดถึงขอบ
    // ผลคือแถบทุกอันต่อกันสนิทกินเต็ม 100% ไม่มีช่องว่างให้เมาส์ตกร่อง
    hitBand(i) {
      const n = this.trendDays.length;
      if (n <= 1) return { left: 0, width: 100 };
      const c = (k) => this.hitLeft(k);
      const left = i === 0 ? 0 : (c(i - 1) + c(i)) / 2;
      const right = i === n - 1 ? 100 : (c(i) + c(i + 1)) / 2;
      return { left, width: Math.max(0, right - left) };
    },
    // กล่อง tooltip ปกติจัดกึ่งกลางบนจุด — วันหัว/ท้ายต้องเลื่อนเข้ามา ไม่งั้นล้นออกนอกการ์ด
    tipShift(i) {
      const x = this.hitLeft(i);
      if (x < 18) return 'translate(0, -8px)';
      if (x > 82) return 'translate(-100%, -8px)';
      return 'translate(-50%, -8px)';
    },
    tipAlign(i) {
      const x = this.hitLeft(i);
      if (x < 18) return 'ta-start';
      if (x > 82) return 'ta-end';
      return 'ta-mid';
    },
    tipText(i) {
      const d = this.trendDays[i];
      if (!d) return '';
      return this.SEVS.map((s) => `${this.$t('ops.sev' + s.charAt(0).toUpperCase() + s.slice(1))} ${d.totals[s]}`).join(', ');
    },
    toggleFilter(sev) { this.filter = this.filter === sev ? '' : sev; },
    toggle(key) { this.expanded = { ...this.expanded, [key]: !this.expanded[key] }; },
    page(key) { return this.pages[key] || 1; },
    setPage(key, p) { this.pages = { ...this.pages, [key]: p }; },
    pagedItems(g) {
      const p = this.page(g.key);
      return g.items.slice((p - 1) * this.perPage, p * this.perPage);
    },
    cols(key) { return COLS[key] || [{ k: 'ref' }]; },
    gotoState(key) { return GOTO[key] || ''; },
    // คีย์จาก backend เป็น 'checkin.group' แต่ t() ใน i18n.js เดินตามจุดเป็นลำดับชั้นของ object
    // ('ops.check.checkin.group' จะถูกอ่านเป็น ops > check > checkin > group แล้วไม่เจอ)
    // จึงแปลงเป็น camelCase ก่อนค้นเสมอ — คีย์ฝั่ง backend คงความหมายเดิมไว้ได้
    i18nKey(key) { return key.replace(/\.(\w)/g, (_, c) => c.toUpperCase()); },
    label(key) { return this.$t('ops.check.' + this.i18nKey(key)); },
    hint(key) { return this.$t('ops.hint.' + this.i18nKey(key)); },
    // ⚠️ ต้องมีทั้ง minimum และ maximum — ใส่แต่ maximum จะได้ 5,200 (ไม่มีทศนิยม) และ
    // 5,200.5 (ตำแหน่งเดียว) · ยอดเงินทุกจุดในระบบแสดง 2 ตำแหน่งเสมอ (เจ้าของสั่ง 2026-08-05)
    baht: formatBaht,
    // คัดลอกเลขที่ไปคลิปบอร์ด — พฤติกรรมเดียวกับหน้าคอร์สของลูกค้า (เงียบทั้งสำเร็จและล้มเหลว:
    // เบราว์เซอร์บางตัวบล็อก clipboard เมื่อไม่ได้อยู่ใน secure context การเด้ง error จะกวนมากกว่าช่วย)
    copyToClipboard(value) {
      if (!value || !navigator.clipboard) return;
      navigator.clipboard.writeText(String(value)).catch(() => {});
    },
    // เหตุผลของการ์ด "สลิปไม่สอดคล้อง" ประกอบจากหลายฟิลด์ จึงไม่มีคอลัมน์ตรง ๆ ใน row
    // (ต้องคิดก่อนด่านเช็คค่าว่างข้างล่าง ไม่งั้นได้ '—' ทุกแถว)
    slipReason(row) {
      const parts = [];
      if (row.amountStatus === 'under' || row.amountStatus === 'over') {
        parts.push(this.$t('ops.slipReason.' + row.amountStatus, { n: this.baht(Math.abs(Number(row.diff) || 0)) }));
      }
      // backend ตั้ง dateMismatch เฉพาะตอนที่มี min_trans_date อยู่แล้ว (ops.js) จึงไม่ต้องเช็คซ้ำ
      if (row.dateMismatch) {
        const same = row.min_trans_date === row.max_trans_date;
        parts.push(this.$t('ops.slipReason.date', {
          slip: this.$formatShortDate(row.min_trans_date) + (same ? '' : `…${this.$formatShortDate(row.max_trans_date)}`),
          paydate: this.$formatShortDate(row.paydate),
        }));
      }
      return parts.length > 0 ? parts.join(' · ') : '—';
    },
    // รายชื่อที่ระบบว่าซ้ำ + เหตุผล — backend ส่ง **โค้ด** (matchKey) มา ไม่ใช่ข้อความไทย
    // เหตุผลจึงถูกแปลตรงนี้ ผู้ใช้ภาษาอื่นไม่เห็นไทยกลางตาราง · ตัดที่ 3 รายชื่อแล้วต่อท้าย +N
    // (1 แถวซ้ำได้ถึง 10 คน ปล่อยไว้จะเป็นข้อความยาวหลายร้อยตัวอักษรในช่องเดียว)
    dupMatches(list) {
      if (!Array.isArray(list) || !list.length) return '—';
      const text = list.slice(0, 3).map((m) => {
        const why = m.matchKey ? this.$t('ops.dupWhy.' + m.matchKey) : (m.why || '');
        return `${m.fullname || m.studentid} [${m.studentid}]${why ? ' — ' + why : ''}`;
      }).join(' · ');
      return list.length > 3 ? `${text} +${list.length - 3}` : text;
    },
    cell(row, c, key) {
      if (c.type === 'slipReason') return this.slipReason(row);
      if (c.type === 'dupMatches') return this.dupMatches(row[c.f || c.k]);
      const v = row[c.f || c.k];
      if (v === null || v === undefined || v === '') return '—';
      // วันที่จาก backend เป็น 'YYYY-MM-DD' อยู่แล้ว — ส่งให้ $formatShortDate ตรง ๆ
      // ห้าม new Date() แล้วฟอร์แมตเอง (timezone ของเครื่องผู้ใช้จะเลื่อนวัน)
      if (c.type === 'date') return this.$formatShortDate(v);
      if (c.type === 'money') return this.baht(v);
      if (c.type === 'paid') return Number(v) === 1 ? this.$t('ops.paidYes') : this.$t('ops.paidNo');
      if (c.type === 'kind') return this.$t('ops.kind.' + v);
      if (c.type === 'method') return this.$t('ops.method.' + v);
      if (c.type === 'age') return this.$t('ops.ageDays', { n: v });
      // ธง 0/1 → ใช่/— (event.unpaid: เกินเดทไลน์ชำระหรือยัง)
      if (c.type === 'flag') return Number(v) === 1 ? this.$t('ops.flagYes') : '—';
      // $t คืน "คีย์" กลับมาเมื่อแปลไม่เจอ — สถานะสลิปตัวใหม่ที่ยังไม่ได้แปลจึงต้องเช็ค
      // ไม่งั้นตารางจะโชว์ 'ops.slipStatus.xxx' ให้ผู้ใช้อ่าน
      if (key === 'slip.unverified' && c.k === 'status') {
        const tk = 'ops.slipStatus.' + v;
        const tx = this.$t(tk);
        return tx === tk ? String(v) : tx;
      }
      return String(v);
    },
  },
};
</script>

<style scoped>
.ops-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
}
.ops-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid var(--c-border);
  background: var(--c-surface);
  cursor: pointer;
  font-family: inherit;
  font-size: 12.5px;
  color: var(--c-text-body);
}
.ops-chip b { font-family: var(--font-head); }
.ops-chip.s-urgent .mdi { color: var(--c-error); }
.ops-chip.s-watch .mdi { color: var(--c-warning); }
.ops-chip.s-info .mdi { color: var(--c-info); }
.ops-chip.on { border-color: var(--c-primary); background: var(--c-surface-3); font-weight: 700; }
.ops-note { margin-top: 10px; }

/* ── การ์ดแนวโน้ม 30 วัน ─────────────────────────────────────────────────── */
.ops-trend { margin-top: 12px; padding: 0 !important; overflow: visible; }
.ops-trend-head {
  display: flex; align-items: center; gap: 12px; width: 100%; padding: 14px 16px;
  border: none; background: transparent; cursor: pointer; font-family: inherit;
}
.ops-trend-head > .mdi:first-child { font-size: 20px; color: var(--c-primary); }
.ops-trend-head .ops-card-t, .ops-trend-head .ops-card-d { display: block; }
.ops-trend-body { padding: 0 16px 14px; }
.ops-trend-legend { display: flex; flex-wrap: wrap; gap: 14px; margin-bottom: 8px; }
.ops-trend-lg { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; color: var(--c-text-muted); }
.ops-trend-lg b { font-family: var(--font-head); color: var(--c-text-heading); font-size: 14px; }
.ops-trend-sw { width: 14px; height: 3px; border-radius: 2px; display: inline-block; }
.ops-trend-sw.s-urgent { background: var(--c-error); }
.ops-trend-sw.s-watch { background: var(--c-warning); }
.ops-trend-sw.s-info { background: var(--c-info); }

/* สูงขึ้นจาก 60px เดิม — เส้นสามเส้นที่ค่าใกล้กันจะได้ไม่ทับกันจนแยกไม่ออก */
.ops-spark-wrap { position: relative; width: 100%; height: 96px; margin-top: 4px; }
.ops-spark { width: 100%; height: 100%; display: block; overflow: visible; }
.ops-spark .s-urgent { stroke: var(--c-error); }
.ops-spark .s-watch { stroke: var(--c-warning); }
.ops-spark .s-info { stroke: var(--c-info); }

/* จุดข้อมูล — ต้อง "เห็น" ว่ามีจุดตรงไหน ไม่ใช่ให้เดาว่าเส้นหักตรงไหน */
.ops-spark-dot {
  position: absolute; width: 7px; height: 7px; border-radius: 999px;
  transform: translate(-50%, -50%); pointer-events: none;
  border: 2px solid var(--c-surface-1); box-sizing: content-box;
  transition: width var(--dur-fast) var(--ease), height var(--dur-fast) var(--ease);
}
.ops-spark-dot.s-urgent { background: var(--c-error); }
.ops-spark-dot.s-watch { background: var(--c-warning); }
.ops-spark-dot.s-info { background: var(--c-info); }
.ops-spark-dot.on { width: 11px; height: 11px; }

.ops-spark-guide {
  position: absolute; top: -2px; bottom: -2px; width: 1px; pointer-events: none;
  background: var(--c-border); transform: translateX(-50%);
}

/* แถบรับเมาส์: 1 แถบต่อ 1 วัน ต่อกันสนิทเต็มความกว้าง + เต็มความสูง
   → เอาเมาส์ไปตรงไหนของกราฟก็ติดวันที่ใกล้ที่สุดเสมอ ไม่ต้องเล็งจุด */
.ops-spark-hit { position: absolute; top: 0; height: 100%; cursor: pointer; background: transparent; }
.ops-spark-hit:focus-visible { outline: 2px solid var(--c-primary); outline-offset: -2px; border-radius: 4px; }

/* same skin as the app-wide hover hint (.sys-tip / .nav-tip in istar-pages.css) */
.ops-spark-tip {
  position: absolute; bottom: 100%; pointer-events: none; z-index: 5;
  display: flex; flex-direction: column; gap: 2px; white-space: nowrap;
  background: #22132f; color: #fff; font-size: 12.5px; font-weight: 600;
  padding: 7px 11px; border-radius: 9px; box-shadow: var(--shadow-md);
}
/* the box slides inward at the first/last day (tipShift) so the arrow has to follow it,
   otherwise it points at empty space instead of the day it describes */
.ops-spark-tip::before { content: ''; position: absolute; top: 100%; border: 6px solid transparent; border-top-color: #22132f; }
.ops-spark-tip.ta-start::before { left: 12px; }
.ops-spark-tip.ta-mid::before { left: 50%; transform: translateX(-50%); }
.ops-spark-tip.ta-end::before { right: 12px; }
.ops-spark-tip span { display: flex; align-items: center; gap: 5px; }
.ops-spark-tip .ops-trend-sw { opacity: .95; }
.spark-toggle {
  display: inline-flex; align-items: center; gap: 2px; margin-top: 8px; padding: 0;
  background: none; border: none; font-size: 11px; color: var(--c-text-muted);
  font-family: var(--font-body); cursor: pointer;
}
.spark-toggle:hover { color: var(--c-primary); }

.ops-card { margin-top: 12px; padding: 0 !important; overflow: visible; }
.ops-card-head {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}
.ops-sev-dot { flex: 0 0 10px; width: 10px; height: 10px; border-radius: 999px; }
.ops-sev-dot.s-urgent { background: var(--c-error); }
.ops-sev-dot.s-watch { background: var(--c-warning); }
.ops-sev-dot.s-info { background: var(--c-info); }
.ops-card-tx { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
.ops-card-t { font-family: var(--font-head); font-weight: 700; color: var(--c-text-heading); font-size: 14.5px; }
.ops-card-d { font-size: 12px; color: var(--c-text-muted); line-height: 1.4; }
.ops-count {
  font-family: var(--font-head);
  font-weight: 700;
  font-size: 17px;
  min-width: 34px;
  text-align: right;
}
.ops-count.s-urgent { color: var(--c-error); }
.ops-count.s-watch { color: var(--c-warning); }
.ops-count.s-info { color: var(--c-info); }
.ops-amount { font-size: 12.5px; color: var(--c-text-muted); white-space: nowrap; }
.ops-chev { color: var(--c-text-muted); font-size: 20px; }
.ops-card-body { padding: 0 16px 14px; border-top: 1px dashed var(--c-border); }
.ops-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px 0;
}

.ops-clear {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 12px;
  padding: 22px 18px;
}
.ops-clear .mdi { font-size: 34px; color: var(--c-success); }
.ops-clear-t { font-family: var(--font-head); font-weight: 700; color: var(--c-text-heading); font-size: 15px; }

.ops-ok { margin-top: 12px; padding: 14px 16px; }
.ops-ok-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: var(--c-text-muted);
  margin-bottom: 8px;
}
.ops-ok-head .mdi { color: var(--c-success); font-size: 17px; }
.ops-ok-list { display: flex; flex-wrap: wrap; gap: 8px; }
.ops-ok-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--c-text-muted);
  background: var(--c-surface-2);
  border-radius: 999px;
  padding: 4px 10px;
}
.ops-ok-item .mdi { color: var(--c-success); font-size: 14px; }
</style>
